import { type EventEmitter, EventListener, type RegistryInterface, isHTMLElement } from "silver";
import { type BookControl, type BookControlMetric, BookControlViewMode } from "core/Controls/Books/BookControl";
import { TeacherBookControl } from "core/Controls/Books/TeacherBookControl";
import { StudentBookControl } from "core/Controls/Books/StudentBookControl";
import { type BookDataService } from "core/Services/BookDataService";
import { type BookModalService } from "core/Services/BookModalService";
import { type BookReadOptions, type ReadBookService } from "core/Services/ReadBookService";
import { type BookARQuizService } from "core/Services/BookARQuizService";
import { type Book } from "core/Data/Book";
import { type BookBundleService } from "shared/Services/BookBundleService";
import { type MyonLanguageLoader } from "Lang";
import { type WindowLayout } from "core/WindowLayout";
import { type BookBundleMenu } from "core/Controls/Books/BookBundleMenu";
import { type PermissionService } from "core/Services/PermissionService";

export interface BookControlService {
	onControlInitialized: EventEmitter<{ control: BookControl, book: Book }>;
	makeBookControl(bookId: number, projectId?: number, callingBand?: number): BookControl;
	setListMetrics(listMetrics: BookControlMetric[]): void;
	setListLabels(listLabels: BookControlMetric[]): void;
}

export class BookControlViewModeService {

	private static storageKey: string = "preferred-view-mode";

	constructor(private storage: RegistryInterface<BookControlViewMode>) { }

	public get preferredViewMode(): BookControlViewMode {
		const storedValue = this.storage.get(BookControlViewModeService.storageKey);
		return storedValue || BookControlViewMode.CARD;
	}

	public set preferredViewMode(mode: BookControlViewMode) {
		this.storage.set(BookControlViewModeService.storageKey, mode);
	}
}

export class StudentBookControlService implements BookControlService {

	public readonly onControlInitialized = EventListener.create<{ control: BookControl, book: Book }>("controlInitialized");
	private controlsById: { [ bookId: number ]: StudentBookControl[] } = { };
	private listMetrics: BookControlMetric[] = [];
	private listLabels: BookControlMetric[] = [];

	constructor(
		private userId: number,
		private showMyList: boolean,
		private bookDataService: BookDataService,
		private bookModalService: BookModalService,
		private bookARQuizService: BookARQuizService,
		private readBookService: ReadBookService,
		private bookBundleService: BookBundleService,
		private lang: MyonLanguageLoader,
		private windowLayout: WindowLayout
	) {
		this.bookBundleService.onBeforeFavoriteUpdate(e => {
			this.controlsById[e.bookId]?.forEach(control => control.onMyList = e.favorited);
		});
	}

	public setListMetrics(listMetrics: BookControlMetric[]): void {
		this.listMetrics = listMetrics;
		Object.values(this.controlsById).forEach(controls => {
			controls.forEach(control => control.setListMetrics(listMetrics));
		});
	}

	public setListLabels(listLabels: BookControlMetric[]): void {
		this.listLabels = listLabels;
		Object.values(this.controlsById).forEach(controls => {
			controls.forEach(control => control.setListLabels(listLabels));
		});
	}

	public makeBookControl(bookId: number, projectId?: number, callingBand?: number): StudentBookControl {
		const control = new StudentBookControl(
			this.showMyList,
			bookId
		);

		if (!this.controlsById.hasOwnProperty(bookId)) {
			this.controlsById[bookId] = [];
		}
		this.controlsById[bookId].push(control);

		Promise.all([
			this.lang.load("Books"),
			this.bookDataService.getFromId(bookId)
		]).then(async ([ lang, book ]) => {
			control.setListMetrics(this.listMetrics);
			control.setListLabels(this.listLabels);

			control.setLang(lang);
			control.data = book;
			control.onFocus(() => {
				const { left, top, width, height } = control.element.getBoundingClientRect();
				this.windowLayout.scrollToBox({
					left:   window.scrollX + left,
					top:    window.scrollY + top,
					width,
					height: height + 200 // for the floating control
				});
			});
			control.onMyList = book.onMyList;
			control.onRequestArQuizLaunch(e => {
				let source: HTMLElement|undefined;
				if (e.originalEvent && isHTMLElement(e.originalEvent.target)) {
					source = e.originalEvent.target;
				}
				this.bookARQuizService.openArQuiz(bookId, { projectId, source });
			});
			control.onRequestInfo(e => {
				const source = e.originalEvent instanceof MouseEvent ? e.originalEvent.target as HTMLElement : undefined;
				this.bookModalService.showBookModal(bookId, {
					projectId,
					source,
					callingBand
				});
			});
			control.onMyListChange(() => {
				this.bookBundleService.setBookIdsInFavorites([ bookId ], control.onMyList, control);
			});
			control.onRequestRead(e => {
				const options: BookReadOptions = { };
				if (projectId) {
					options.projectId = projectId;
				}
				if (callingBand) {
					options.callingBand = callingBand;
				}
				this.readBookService.readBookFromEvent(book.archiveCode, e.originalEvent, options);
			});
			control.setArQuizAvailable(await this.bookARQuizService.isBookAndUserligible(
				book,
				this.userId,
				projectId
			));
			this.onControlInitialized.trigger({ control, book });
		});

		return control;
	}
}

export function isBookBagAwareBookControlService(service: BookControlService): service is BookBagAwareBookControlService {
	return "onRequestAddToBookBag" in service;
}

interface BookBagAwareBookControlService extends BookControlService {
	readonly onRequestAddToBookBag: EventEmitter<{ control: BookControl, book: Book }>;
}

export class TeacherBookControlService implements BookBagAwareBookControlService {

	public readonly onControlInitialized = EventListener.create<{ control: BookControl, book: Book }>("controlInitialized")
	public readonly onRequestAddToBookBag = EventListener.create<{ control: BookControl, book: Book }>("requestAddToBookBag");
	private listMetrics: BookControlMetric[] = [];
	private listLabels: BookControlMetric[] = [];
	private cardMetrics: BookControlMetric[] = [];
	private controlsById: { [ bookId: number ]: TeacherBookControl[] } = { };

	constructor(
		private bookDataService: BookDataService,
		private bookModalService: BookModalService,
		private readBookService: ReadBookService,
		private bookBundleService: BookBundleService,
		private bookBundleMenu: BookBundleMenu,
		private lang: MyonLanguageLoader,
		private permissionService: PermissionService,
	) {
		this.bookBundleService.onBeforeFavoriteUpdate(e => {
			this.controlsById[e.bookId]?.forEach(control => control.onMyList = e.favorited);
		});
	}

	public setListMetrics(listMetrics: BookControlMetric[]): void {
		this.listMetrics = listMetrics;
		Object.entries(this.controlsById).forEach(([, controls]) => {
			controls.forEach(control => control.setListMetrics(listMetrics));
		});
	}

	public setListLabels(listLabels: BookControlMetric[]): void {
		this.listLabels = listLabels;
		Object.values(this.controlsById).forEach(controls => {
			controls.forEach(control => control.setListLabels(listLabels));
		});
	}

	public setCardMetrics(cardMetrics: BookControlMetric[]): void {
		this.cardMetrics = cardMetrics;
		Object.entries(this.controlsById).forEach(([, controls]) => {
			controls.forEach(control => control.setCardMetrics(cardMetrics));
		});
	}

	public makeBookControl(bookId: number, projectId?: number, callingBand?: number): TeacherBookControl {
		const control = new TeacherBookControl(bookId, !this.permissionService.isSharedLogin());

		if (!this.controlsById.hasOwnProperty(bookId)) {
			this.controlsById[bookId] = [];
		}
		this.controlsById[bookId].push(control);

		Promise.all([
			this.lang.load("Books"),
			this.bookDataService.getFromId(bookId)
		]).then(([ lang, book ]) => {

			control.setLang(lang);
			control.data = book;
			control.onMyList = book.onMyList;

			control.onRequestAssign(() => this.onRequestAddToBookBag.trigger({ control, book }));

			control.setCardMetrics(this.cardMetrics);
			control.setListMetrics(this.listMetrics);
			control.setListLabels(this.listLabels);

			control.onRequestInfo(() => {
				this.bookModalService.showBookModal(bookId, {
					source: control.element,
					projectId,
					callingBand,
				}).catch(console.error);
			});
			control.onMyListChange(async (e) => {
				await this.bookBundleService.setBookIdsInFavorites([bookId], control.onMyList, control).catch(console.error);
				if (control.onMyList) {
					this.bookBundleMenu.showMenu(e.source, bookId);
				}
			});
			control.onRequestRead(() => {
				this.readBookService.readBook(book.archiveCode, {
					source: control.element,
					callingBand,
					projectId
				});
			});

			this.onControlInitialized.trigger({ control, book });
		});

		return control;
	}
}
